import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../lib/adminAuth';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, Loader2, Save, ImageIcon } from 'lucide-react';

const EMPTY = {
  name: '', builder: '', builder_logo: '', image: '', images_text: '',
  type: 'apartment', facing: 'North Facing', furnishing: 'raw', status: 'Under Construction',
  bhk: '2 BHK', location: '', city: 'Gurgaon', sqft: '', floor: '', price: '', old_price: '',
  negotiable: false, listing_type: 'resale', description: '', amenities_text: '',
};

const AdminPropertyEditorPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    adminApi.get('/properties', { params: { limit: 500 } })
      .then((r) => {
        const found = (r.data.items || []).find((p) => p.id === id);
        if (!found) { toast({ title: 'Property not found' }); navigate('/admin/properties'); return; }
        setForm({
          ...EMPTY,
          ...found,
          images_text: (found.images || []).join('\n'),
          amenities_text: (found.amenities || []).join('\n'),
          sqft: found.sqft ?? '',
          old_price: found.old_price ?? '',
        });
      })
      .catch(() => toast({ title: 'Failed to load' }))
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.builder || !form.image || !form.location || !form.city || !form.price) {
      toast({ title: 'Missing required fields', description: 'Name, Builder, Image, Location, City & Price are required.' });
      return;
    }
    const payload = {
      name: form.name, builder: form.builder, builder_logo: form.builder_logo,
      image: form.image,
      images: (form.images_text || '').split('\n').map((s) => s.trim()).filter(Boolean),
      type: form.type, facing: form.facing, furnishing: form.furnishing, status: form.status,
      bhk: form.bhk, location: form.location, city: form.city,
      sqft: parseInt(form.sqft) || 0, floor: form.floor, price: form.price,
      old_price: form.old_price || null, negotiable: !!form.negotiable,
      listing_type: form.listing_type, description: form.description,
      amenities: (form.amenities_text || '').split('\n').map((s) => s.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.put(`/properties/${id}`, payload);
        toast({ title: 'Property updated', description: form.name });
      } else {
        await adminApi.post('/properties', payload);
        toast({ title: 'Property created', description: form.name });
      }
      navigate('/admin/properties');
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return <AdminLayout title="Loading..."><div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9a1b2f]" /></div></AdminLayout>;
  }

  return (
    <AdminLayout
      title={isEdit ? 'Edit Property' : 'Add New Property'}
      actions={<Link to="/admin/properties"><Button variant="outline" size="sm"><ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back</Button></Link>}
    >
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {/* Basics */}
          <Section title="Basic Information">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Property Name *"><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="M3M Crown" /></Field>
              <Field label="Builder *"><Input value={form.builder} onChange={(e) => set('builder', e.target.value)} placeholder="M3M" /></Field>
              <Field label="Builder Logo URL"><Input value={form.builder_logo} onChange={(e) => set('builder_logo', e.target.value)} placeholder="https://..." /></Field>
              <Field label="Listing Type *">
                <Select value={form.listing_type} onValueChange={(v) => set('listing_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resale">Resale</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="resale_commercial">Resale Commercial</SelectItem>
                    <SelectItem value="rent_commercial">Rent Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="BHK / Config *"><Input value={form.bhk} onChange={(e) => set('bhk', e.target.value)} placeholder="2 BHK / Retail / Food Court" /></Field>
              <Field label="Type">
                <Select value={form.type} onValueChange={(v) => set('type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="floor">Floor</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="shop">Shop</SelectItem>
                    <SelectItem value="food court">Food Court</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          {/* Location */}
          <Section title="Location & Specifications">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Location *"><Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Sector 89, Gurgaon" /></Field>
              <Field label="City *"><Input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Gurgaon" /></Field>
              <Field label="Area (sqft)"><Input type="number" value={form.sqft} onChange={(e) => set('sqft', e.target.value)} placeholder="1200" /></Field>
              <Field label="Floor"><Input value={form.floor} onChange={(e) => set('floor', e.target.value)} placeholder="12 Floor" /></Field>
              <Field label="Facing">
                <Select value={form.facing} onValueChange={(v) => set('facing', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['North Facing','North East Facing','East Facing','South East Facing','South Facing','South West Facing','West Facing','North West Facing'].map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Furnishing">
                <Select value={form.furnishing} onValueChange={(v) => set('furnishing', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="raw">Raw</SelectItem>
                    <SelectItem value="semi-furnished">Semi Furnished</SelectItem>
                    <SelectItem value="furnished">Furnished</SelectItem>
                    <SelectItem value="wareshell">Wareshell</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => set('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Under Construction">Under Construction</SelectItem>
                    <SelectItem value="Ready to Move">Ready to Move</SelectItem>
                    <SelectItem value="New Launch">New Launch</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          {/* Pricing */}
          <Section title="Pricing">
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Price *"><Input value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="₹1.75 Cr" /></Field>
              <Field label="Old Price"><Input value={form.old_price} onChange={(e) => set('old_price', e.target.value)} placeholder="2.0 Cr" /></Field>
              <Field label="Negotiable">
                <div className="h-10 flex items-center gap-2">
                  <Switch checked={!!form.negotiable} onCheckedChange={(v) => set('negotiable', v)} />
                  <span className="text-sm text-gray-600">{form.negotiable ? 'Yes' : 'No'}</span>
                </div>
              </Field>
            </div>
          </Section>

          {/* Media */}
          <Section title="Media">
            <Field label="Main Image URL *"><Input value={form.image} onChange={(e) => set('image', e.target.value)} placeholder="https://.../hero.jpg" /></Field>
            <Field label="Additional Image URLs (one per line)">
              <Textarea rows={4} value={form.images_text} onChange={(e) => set('images_text', e.target.value)} placeholder={'https://.../img1.jpg\nhttps://.../img2.jpg'} />
            </Field>
          </Section>

          {/* Content */}
          <Section title="Description & Amenities">
            <Field label="Description">
              <Textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="About this property..." />
            </Field>
            <Field label="Amenities (one per line)">
              <Textarea rows={5} value={form.amenities_text} onChange={(e) => set('amenities_text', e.target.value)} placeholder={'Swimming Pool\nGym\nClubhouse'} />
            </Field>
          </Section>
        </div>

        {/* Preview + Save */}
        <aside className="space-y-4 lg:sticky lg:top-24 h-fit">
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
              {form.image
                ? <img src={form.image} alt="" className="w-full h-full object-cover" />
                : <div className="text-gray-400 text-xs flex flex-col items-center gap-2"><ImageIcon className="w-8 h-8" /> Preview</div>}
            </div>
            <div className="p-4">
              <div className="font-bold text-gray-900 line-clamp-1">{form.name || 'Property Name'}</div>
              <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{form.location || 'Location'}</div>
              <div className="mt-2 text-lg font-black text-[#9a1b2f]">{form.price || '₹ Price'}</div>
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full h-11 bg-[#9a1b2f] hover:bg-[#7a1524]">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {isEdit ? 'Update Property' : 'Create Property'}
          </Button>
          <Link to="/admin/properties"><Button type="button" variant="outline" className="w-full">Cancel</Button></Link>
        </aside>
      </form>
    </AdminLayout>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5">
    <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminPropertyEditorPage;
